import React, { useEffect, useState, useRef } from "react";
import LabCard from "./LabCard";
import { fetchNearbyLabsData } from "../../services/LabTestApi/LabList";
import { useLocationContext } from "../../context/LocationContext";
import { useNavigate, useLocation } from "react-router-dom";
import { FaChevronLeft, FaChevronRight, FaStar } from "react-icons/fa";
import { Search, MapPin, Navigation, X, Flame } from "lucide-react";

export default function LabList() {
  const { coords, address, loading: locationLoading } = useLocationContext();
  const navigate = useNavigate();
  const location = useLocation();

  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [isHovered, setIsHovered] = useState(false);

  const scrollRef = useRef(null);
  const autoScrollInterval = useRef(null);

  const from = location.state?.from || "normal";

  const filteredLabs = labs.filter((lab) =>
    lab.name?.toLowerCase().includes(search.toLowerCase())
  );

  const isSearchActive = search.length > 0;
  const hasNoSearchResults = isSearchActive && filteredLabs.length === 0;
  const hasLabsToShow = filteredLabs.length > 0;

  // API Call
  useEffect(() => {
    const getLabs = async () => {
      try {
        console.log("🚀 LabList trigger hua");
        console.log("📍 coords:", coords);

        if (!coords?.lat || !coords?.lng) {
          console.log("⚠️ coords missing, waiting...");
          return;
        }

        setLoading(true);

        const data = await fetchNearbyLabsData(
          Number(coords.lat),
          Number(coords.lng)
        );

        console.log("📦 API response:", data);
        setLabs(data || []);
      } catch (err) {
        console.log("❌ error:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    getLabs();
  }, [coords?.lat, coords?.lng]);

  // Auto Scroll Effect
  useEffect(() => {
    if (!hasLabsToShow || filteredLabs.length <= 2) return;
    
    const startAutoScroll = () => {
      if (autoScrollInterval.current) clearInterval(autoScrollInterval.current);
      
      autoScrollInterval.current = setInterval(() => {
        if (scrollRef.current && !isHovered && filteredLabs.length > 0) {
          const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
          
          if (scrollLeft + clientWidth >= scrollWidth - 10) {
            scrollRef.current.scrollTo({ left: 0, behavior: 'smooth' });
          } else {
            scrollRef.current.scrollBy({ left: 260, behavior: 'smooth' });
          }
        }
      }, 3000);
    };

    startAutoScroll();
    return () => {
      if (autoScrollInterval.current) clearInterval(autoScrollInterval.current);
    };
  }, [isHovered, filteredLabs.length, hasLabsToShow]);

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -260, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 260, behavior: "smooth" });
    }
  };

  const clearSearch = () => {
    setSearch("");
  };

  if (locationLoading || loading) {
    return (
      <div className="px-4 md:px-8 py-6 bg-gradient-to-br from-gray-50 via-white to-red-50/30 rounded-xl shadow-lg mt-3">
        <div className="flex flex-col items-center justify-center gap-3 py-12">
          <div className="w-10 h-10 border-4 border-red-500 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-gray-500 text-sm">Finding labs near you...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 md:px-8 py-6 bg-gradient-to-br from-gray-50 via-white to-red-50/30 rounded-xl shadow-lg mt-3">
        <div className="text-center py-12">
          <p className="text-red-500 font-medium">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg text-sm hover:shadow-lg transition-all"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Agar koi labs hi nahi hai
  if (labs.length === 0 && !loading) {
    return (
      <div className="px-4 md:px-8 py-6 bg-gradient-to-br from-gray-50 via-white to-red-50/30 rounded-xl shadow-lg mt-3">
        
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-1.5 rounded-full shadow-md mb-3">
            <Flame size={12} className="text-yellow-400" />
            <span className="text-xs font-bold tracking-wide">NEARBY LABS</span>
            <FaStar size={12} className="text-yellow-400" />
          </div>
          
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold">
            <span className="bg-gradient-to-r from-gray-800 to-gray-900 bg-clip-text text-transparent">
              MedrphaLabs
            </span>{" "}
            <span className="bg-gradient-to-r from-red-600 to-red-800 bg-clip-text text-transparent">
              Near You
            </span>
          </h2>
          
          <div className="flex items-center justify-center gap-2 text-gray-500 text-sm mt-2">
            <Navigation size={14} className="text-red-500" />
            <span className="truncate">{address || "Your location"}</span>
          </div>
          
          {/* Decorative dots */}
          <div className="flex justify-center gap-2 mt-4">
            <div className="w-1.5 h-1.5 rounded-full bg-red-300"></div>
            <div className="w-6 h-1.5 rounded-full bg-gradient-to-r from-red-500 to-red-600"></div>
            <div className="w-1.5 h-1.5 rounded-full bg-red-300"></div>
            <div className="w-1.5 h-1.5 rounded-full bg-red-200"></div>
          </div>
        </div>
        
        <div className="text-center py-12 bg-white/50 rounded-2xl">
          <div className="text-gray-400 mb-3">
            <MapPin size={48} className="mx-auto" />
          </div>
          <p className="text-gray-500 font-medium">No labs available in your area</p>
          <p className="text-gray-400 text-sm mt-1">Try changing your location</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-8 py-6 bg-gradient-to-br from-gray-50 via-white to-red-50/30 rounded-xl shadow-lg mt-3">
      
      {/* Header Section */}
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-2 bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-1.5 rounded-full shadow-md mb-3">
          <Flame size={12} className="text-yellow-400" />
          <span className="text-xs font-bold tracking-wide">NEARBY LABS</span>
          <FaStar size={12} className="text-yellow-400" />
        </div>
        
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold">
          <span className="bg-gradient-to-r from-gray-800 to-gray-900 bg-clip-text text-transparent">
            MedrphaLabs
          </span>{" "}
          <span className="bg-gradient-to-r from-red-600 to-red-800 bg-clip-text text-transparent">
            Near You
          </span>
        </h2>
        
        <div className="flex items-center justify-center gap-2 text-gray-500 text-sm mt-2">
          <Navigation size={14} className="text-red-500" />
          <span className="truncate">{address || "Fetching location..."}</span>
        </div>
        
        {/* Decorative dots */}
        <div className="flex justify-center gap-2 mt-4">
          <div className="w-1.5 h-1.5 rounded-full bg-red-300"></div>
          <div className="w-6 h-1.5 rounded-full bg-gradient-to-r from-red-500 to-red-600"></div>
          <div className="w-1.5 h-1.5 rounded-full bg-red-300"></div>
          <div className="w-1.5 h-1.5 rounded-full bg-red-200"></div>
          <div className="w-1.5 h-1.5 rounded-full bg-red-200"></div>
        </div>
      </div>

      {/* Main Container */}
      <div 
        className="relative bg-white rounded-xl shadow-lg p-4 md:p-5"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Search Bar */}
        <div className="mb-5">
          <div className="relative max-w-md mx-auto">
            <Search
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              placeholder="Search lab by name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-11 pr-11 py-2.5 rounded-full border border-gray-200 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent transition-all text-sm"
            />
            {search && (
              <button
                onClick={clearSearch}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Search Results UI */}
        {hasNoSearchResults ? (
          <div className="text-center py-10">
            <div className="text-gray-400 mb-3">
              <Search size={48} className="mx-auto" />
            </div>
            <p className="text-gray-500 font-medium">No labs found matching "{search}"</p>
            <p className="text-gray-400 text-sm mt-1">Try searching with different keywords</p>
            <button
              onClick={clearSearch}
              className="mt-4 px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg text-sm hover:shadow-lg transition-all"
            >
              Clear Search
            </button>
          </div>
        ) : !hasLabsToShow ? (
          <div className="text-center py-10">
            <div className="text-gray-400 mb-2">
              <MapPin size={48} className="mx-auto" />
            </div>
            <p className="text-gray-500">No labs found in your area</p>
          </div>
        ) : (
          <>
            {/* Navigation Buttons */}
            {filteredLabs.length > 2 && (
              <>
                <button
                  onClick={scrollLeft}
                  className="absolute left-1 top-1/2 -translate-y-1/2 z-20 bg-white shadow-md p-2 rounded-full hover:bg-red-500 hover:text-white transition-all duration-300 border border-gray-200"
                  aria-label="Scroll left"
                >
                  <FaChevronLeft size={14} />
                </button>

                <button
                  onClick={scrollRight}
                  className="absolute right-1 top-1/2 -translate-y-1/2 z-20 bg-white shadow-md p-2 rounded-full hover:bg-red-500 hover:text-white transition-all duration-300 border border-gray-200"
                  aria-label="Scroll right"
                >
                  <FaChevronRight size={14} />
                </button>
              </>
            )}

            {/* Horizontal Scroll Container - Less Space Between Cards */}
            <div
              ref={scrollRef}
              className="flex gap-3 overflow-x-auto pb-2 hide-scrollbar"
              style={{
                scrollBehavior: 'smooth',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch'
              }}
            >
              {filteredLabs.map((lab, index) => (
                <div
                  key={lab.id || index}
                  className="min-w-[240px] md:min-w-[260px] cursor-pointer transition-all duration-300 hover:scale-105 hover:-translate-y-1"
                  onClick={() => {
                    console.log("🔥 NAVIGATE TO LAB:", lab.name);
                    navigate(`/lab/${lab.id}`, {
                      state: { lab, from },
                    });
                  }}
                >
                  <LabCard lab={lab} />
                </div>
              ))}
            </div>

            {/* Scroll Indicator */}
            {filteredLabs.length > 2 && (
              <div className="flex justify-center items-center gap-2 mt-3">
                <div className="w-8 h-1 bg-red-400 rounded-full animate-pulse"></div>
                <div className="text-[10px] text-gray-400 flex items-center gap-1">
                  <span>Auto Scroll</span>
                  <FaChevronRight size={10} />
                </div>
              </div>
            )}

            {/* Labs Count */}
            <div className="text-center mt-3">
              <p className="text-[10px] text-gray-400">
                Showing {filteredLabs.length} of {labs.length} labs
                {isSearchActive && ` (matching "${search}")`}
              </p>
            </div>
          </>
        )}
      </div>

      {/* Trust Badges */}
      {labs.length > 0 && (
        <div className="mt-5 pt-3 border-t border-gray-200">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div className="flex items-center justify-center gap-1.5 text-[10px] text-gray-600 bg-white/50 rounded-lg p-1.5 hover:bg-white transition-all duration-300">
              <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-green-600 text-[9px] font-bold">✓</span>
              </div>
              <span>NABL Certified Labs</span>
            </div>
            <div className="flex items-center justify-center gap-1.5 text-[10px] text-gray-600 bg-white/50 rounded-lg p-1.5 hover:bg-white transition-all duration-300">
              <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 text-[9px] font-bold">⚡</span>
              </div>
              <span>Free Home Pickup</span>
            </div>
            <div className="flex items-center justify-center gap-1.5 text-[10px] text-gray-600 bg-white/50 rounded-lg p-1.5 hover:bg-white transition-all duration-300">
              <div className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-purple-600 text-[9px] font-bold">🏆</span>
              </div>
              <span>Accurate Reports</span>
            </div>
            <div className="flex items-center justify-center gap-1.5 text-[10px] text-gray-600 bg-white/50 rounded-lg p-1.5 hover:bg-white transition-all duration-300">
              <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center">
                <span className="text-red-600 text-[9px] font-bold">❤️</span>
              </div>
              <span>24/7 Support</span>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.05); }
        }
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
}